import { Link, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { SeoHead } from "@/components/SeoHead"
import { organizationNode, websiteNode, personHarryNode, podcastSeriesNodes } from "@/lib/seo"
import type { PageProps } from "@/types/inertia"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section, Eyebrow } from "@/components/marketing/sections/Section"

const DESCRIPTION =
  "FullCast is a boutique, founder-led podcast agency. Meet Harry Duran and the relationship-driven approach behind 130+ launched shows since 2015."

export default function About() {
  const base = (usePage<PageProps>().props.app_url || "https://fullcast.co").replace(/\/$/, "")

  return (
    <MarketingLayout>
      <SeoHead
        title="About FullCast"
        description={DESCRIPTION}
        jsonLd={[organizationNode(base), websiteNode(base), personHarryNode(base), ...podcastSeriesNodes(base)]}
      />

      <Section>
        <div className="max-w-2xl">
          <Eyebrow>About</Eyebrow>
          <h1 className="display-1 mt-4">
            A boutique podcast partner for established experts
          </h1>
          <p className="mt-6 text-lg text-ink-body">
            FullCast helps founders, coaches, consultants, and advisors use
            podcasting to build authority, create trust-rich content, and develop
            strategic relationships through better conversations.
          </p>
        </div>

        <div className="mt-12 grid gap-10 lg:grid-cols-[1.4fr_1fr] lg:gap-14">
          <div className="body-content max-w-2xl">
            <p>
              We started FullCast in 2015 with a simple belief: a podcast is most
              valuable when it gives you a reason to have better conversations
              with the right people. Since then, we've helped launch more than 130
              shows for expert-led brands — and we've watched the best of them
              become engines for authority, trust, and relationships rather than
              just another content channel.
            </p>
            <p>
              Most agencies sell production. We focus on the conversation: the
              positioning behind your show, the guests worth building around, the
              preparation that makes each interview count, the production that
              makes it polished, and the content that extends every episode's
              value.
            </p>
            <h2>Founder-led, by design</h2>
            <p>
              FullCast is intentionally boutique. The work is led by Harry Duran,
              who has hosted <a href="https://podcastjunkies.com">Podcast Junkies</a> since 2014 and founded FullCast in
              2015. Harry also hosts the{" "}
              <a href="https://igrownews.com/podcast/vertical-farming">Vertical Farming Podcast</a>, where a niche,
              relationship-driven strategy has generated more than $100K in
              sponsorship revenue — proof of the same approach we bring to every
              partnership.
            </p>
            <p>
              You're not handed off to a bloated production machine or a junior
              account manager. You work with a small, experienced team that treats
              your show as a strategic relationship asset.
            </p>
          </div>

          <aside className="lg:pt-2">
            <div className="rounded-xl border border-hairline bg-surface p-7">
              <h3>By the numbers</h3>
              <dl className="mt-5 space-y-4">
                <div>
                  <dt className="text-sm text-ink-muted">Shows launched</dt>
                  <dd className="font-display text-2xl font-semibold text-ink-display">130+</dd>
                </div>
                <div>
                  <dt className="text-sm text-ink-muted">Podcasting since</dt>
                  <dd className="font-display text-2xl font-semibold text-ink-display">2014</dd>
                </div>
                <div>
                  <dt className="text-sm text-ink-muted">Sponsorship experience</dt>
                  <dd className="font-display text-2xl font-semibold text-ink-display">$100K+</dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>

        <div className="mt-14">
          <Button asChild size="lg">
            <Link href="/apply">Apply for a Podcast Growth Fit Call</Link>
          </Button>
        </div>
      </Section>
    </MarketingLayout>
  )
}
